import { Loader } from "@mantine/core";
import {
  type ColumnDef,
  type ColumnPinningState,
  type RowSelectionState,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useMemo,
  useState,
} from "react";

import type { Nullable } from "@/types/utils";

/**
 * Table hook for the main server-paginated list views (screeners, locations,
 * assigned-to, session results). For simpler tables (review page, manage
 * locations), use TanStack Table directly with a component-local setup.
 */
export interface UseServerPaginatedTableProps<T> {
  columns: ColumnDef<T>[];
  columnPinning: ColumnPinningState;
  columnVisibility?: Record<string, boolean>;
  data: T[];
  isLoading: boolean;
  totalPages: number;
}

export interface UseServerPaginatedTableReturn<T> {
  loadingFallback: Nullable<ReactNode>;
  table: ReturnType<typeof useReactTable<T>>;
  rowSelection: RowSelectionState;
  setRowSelection: Dispatch<SetStateAction<RowSelectionState>>;
  selectedRows: T[];
}

export const useServerPaginatedTable = <T,>({
  columns,
  columnPinning,
  columnVisibility = {},
  data,
  isLoading,
  totalPages,
}: UseServerPaginatedTableProps<T>): UseServerPaginatedTableReturn<T> => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const loadingFallback = useMemo(
    () =>
      isLoading ? (
        <div className="w-full h-dvh flex justify-center items-center">
          <Loader color="sky-blue.3" />
        </div>
      ) : null,
    [isLoading],
  );

  const table = useReactTable<T>({
    columns,
    data,
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    onRowSelectionChange: setRowSelection,
    pageCount: totalPages,
    state: {
      columnPinning,
      columnVisibility,
      rowSelection,
    },
  });

  const selectedRows = table
    .getSelectedRowModel()
    .rows.map((row) => row.original);

  return {
    loadingFallback,
    rowSelection,
    selectedRows,
    setRowSelection,
    table,
  };
};
